import { PunctElement, TextElement, TranscriptElement } from '../models/TranscriptElement';

/**
 * Manages operations on transcript elements for natural text editing
 * 
 * This manager provides utility functions for modifying transcript elements
 * during text editing operations like splitting, merging, and pasting.
 */
export class ElementOperationManager {
  /**
   * Generates a new unique element ID
   * 
   * @returns A new unique ID for a transcript element
   */
  private generateElementId(): string {
    return `element_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Splits an element at a specific character position
   * 
   * @param element The element to split
   * @param position The character position to split at
   * @returns Two new elements created from the split
   */
  splitElement(
    element: TranscriptElement, 
    position: number
  ): [TranscriptElement, TranscriptElement] {
    const value = element.value || '';
    
    // Create two new elements
    const firstPart: TranscriptElement = {
      ...element,
      id: this.generateElementId(),
      value: value.substring(0, position)
    };
    
    const secondPart: TranscriptElement = {
      ...element,
      id: this.generateElementId(),
      value: value.substring(position)
    };
    
    // For text elements, split the timing
    if (element.type === 'text') {
      const textEl = element as TextElement;
      if (textEl.startTime !== undefined && textEl.endTime !== undefined) {
        const totalDuration = textEl.endTime - textEl.startTime;
        const ratio = position / value.length;
        
        // Distribute time proportionally to length
        (firstPart as TextElement).startTime = textEl.startTime;
        (firstPart as TextElement).endTime = textEl.startTime + (totalDuration * ratio);
        
        (secondPart as TextElement).startTime = (firstPart as TextElement).endTime;
        (secondPart as TextElement).endTime = textEl.endTime;
      }
    }
    
    return [firstPart, secondPart];
  }
  
  /**
   * Merges two consecutive elements
   * 
   * @param first The first element
   * @param second The second element
   * @returns A new merged element
   */
  mergeElements(
    first: TranscriptElement, 
    second: TranscriptElement
  ): TranscriptElement {
    const mergedElement: TranscriptElement = {
      ...first,
      value: (first.value || '') + (second.value || '')
    };
    
    // For text elements, merge the timing
    if (first.type === 'text' && second.type === 'text') {
      const firstEl = first as TextElement;
      const secondEl = second as TextElement;
      
      if (firstEl.startTime !== undefined && secondEl.endTime !== undefined) {
        (mergedElement as TextElement).startTime = firstEl.startTime;
        (mergedElement as TextElement).endTime = secondEl.endTime;
      }
    }
    
    return mergedElement;
  }
  
  /**
   * Handles paste into an element
   * 
   * @param element The element being pasted into
   * @param text The text being pasted
   * @param position The position within the element to paste
   * @returns Array of new elements resulting from the paste
   */
  handlePaste(
    element: TranscriptElement,
    text: string,
    position: number
  ): TranscriptElement[] {
    // Split the element at the paste position
    const [beforePaste, afterPaste] = this.splitElement(element, position);
    
    // Create elements for the pasted text
    const pastedElements = this.tokenizeText(text).map(token => {
      return {
        id: this.generateElementId(),
        type: this.isPunctuation(token) ? 'punct' : 'text',
        value: token
      } as TranscriptElement;
    });
    
    // Distribute timing across new elements
    if (element.type === 'text') {
      const textEl = element as TextElement;
      if (textEl.startTime !== undefined && textEl.endTime !== undefined) {
        const totalTime = textEl.endTime - textEl.startTime;
        const totalLength = (beforePaste.value?.length || 0) + 
                          text.length + 
                          (afterPaste.value?.length || 0);
        
        // Calculate time per character
        const timePerChar = totalTime / totalLength;
        
        // Start from beforePaste end time
        let currentTime = (beforePaste as TextElement).endTime || textEl.startTime;
        
        // Assign timing to each pasted element
        pastedElements.forEach(el => {
          if (el.type === 'text') {
            (el as TextElement).startTime = currentTime;
            currentTime += (el.value?.length || 0) * timePerChar;
            (el as TextElement).endTime = currentTime;
          }
        });
        
        // Update afterPaste timing
        if (afterPaste.type === 'text') {
          (afterPaste as TextElement).startTime = currentTime;
        }
      }
    }
    
    // Return all elements in order
    return [beforePaste, ...pastedElements, afterPaste];
  }
  
  /**
   * Check if a string is punctuation
   * 
   * @param text The text to check
   * @returns True if the text is punctuation, false otherwise
   */
  isPunctuation(text: string): boolean {
    return /^[^\w\s]$/.test(text);
  }
  
  /**
   * Tokenize text into words and punctuation
   * 
   * @param text The text to tokenize
   * @returns Array of tokens (words and punctuation)
   */
  tokenizeText(text: string): string[] {
    // More sophisticated tokenization that preserves punctuation and spaces
    const tokens: string[] = [];
    
    // Replace multiple spaces with a single space to normalize
    const normalizedText = text.replace(/\s+/g, ' ');
    
    // Step 1: Split by spaces but keep spaces as tokens
    const spaceSplit = normalizedText.split(/(\s)/);
    
    // Step 2: For each space-separated part, extract punctuation
    spaceSplit.forEach(part => {
      if (!part) return;
      
      // If it's just a space, add it directly
      if (part === ' ') {
        // Skip spaces as they'll be handled automatically between elements
        return;
      }
      
      // Break out punctuation from words
      let current = part;
      let match;
      
      // Extract leading punctuation
      while ((match = /^([^\w\s])(.*)$/.exec(current))) {
        tokens.push(match[1]); // Add punctuation
        current = match[2];    // Continue with the rest
      }
      
      // If there's non-punctuation content left
      if (current) {
        // Check for embedded punctuation (like "word," or "word!")
        const parts = current.split(/([^\w\s])/);
        parts.forEach(p => {
          if (p) tokens.push(p);
        });
      }
    });
    
    // Filter out empty tokens
    return tokens.filter(token => token.trim() !== '');
  }
}